// ============================================================================
// LE CODE ARTICLE D'UN ARTICLE QUI NAÎT — proposé libre, et nommé quand il est pris.
//
// UTILITÉ : les deux assistants de création (« Article simple », « Gamme ») et la fenêtre « Dupliquer » proposent un code
// article. Le code est la clé du MODÈLE : sous un code déjà porté, la création s'ajoutait à l'article qui le porte et ses
// champs écrasaient l'original (deux désignations longues qui commencent pareil donnent le même code, tronqué à 24
// caractères). Ce module dit, AVANT le clic, quel code partira et qui porte celui qu'on a saisi.
//
// ⚠️ LE SERVEUR EST LA RÈGLE : il refuse un code pris au moment d'écrire, même si cet écran ne l'a pas vu (second onglet,
//    lecture en échec). Ce qui est dit ici n'est qu'une courtoisie — mais une courtoisie qui évite le seul cas grave.
// ⚠️ SANS RÉPONSE, ON NE BLOQUE PAS : une lecture en échec se DIT, et l'écriture tranchera avec son motif.
// ============================================================================
import { useEffect, useState } from 'react';
import { proposeArticleCode } from '@orgazone/core-catalog';
import { api, type ArticleCodeHolder } from '../api';
import { t } from '../i18n';

/** Ce que l'écran sait du code qui partira. */
export interface ArticleCodeCheck {
  /** Le code qui partira : celui qu'on a saisi, sinon le premier libre à partir de la désignation. */
  effective: string;
  /** Le code proposé quand on n'a rien saisi — affiché dans le champ vide. */
  proposed: string;
  /** L'article qui porte le code saisi — ou, sans saisie, celui qui porte le code tiré de la désignation. */
  holder: ArticleCodeHolder | null;
  /** Le premier code libre à partir du code saisi quand il est pris — ce que « Prendre … » pose. */
  freeAlternative: string | null;
  /** La vérification n'est pas encore revenue. */
  checking: boolean;
  /**
   * La création doit attendre : code SAISI en cours de vérification, ou déjà porté.
   * ⚠️ LE CODE PROPOSÉ NE BLOQUE JAMAIS : tant que la vérification n'est pas revenue, c'est le code tiré de la désignation qui
   *    part, et le serveur le refuse en nommant l'article s'il est pris — l'avis propose alors le code libre.
   */
  blocks: boolean;
  /** Le motif d'une vérification impossible — l'écran le dit, l'écriture tranchera. */
  failure: string | null;
}

/** Délai de frappe avant de demander au serveur : une lecture par mot, pas une par lettre. */
const CHECK_DELAY_MS = 250;

/**
 * VÉRIFIE le code d'un article qui naît, à chaque frappe (après un court délai).
 * `name` donne le code proposé quand `typed` est vide — sauf `proposedBase`, la base imposée (la duplication part du code
 * de l'article d'origine tant que la désignation n'a pas changé) ; `active` faux suspend tout (fenêtre fermée).
 */
export function useArticleCodeCheck(active: boolean, name: string, typed: string, proposedBase?: string): ArticleCodeCheck {
  const saisi = typed.trim();
  const base = saisi || proposedBase || (name.trim() ? proposeArticleCode(name) : '');
  const [reply, setReply] = useState<{ base: string; code: string; free: boolean; holder: ArticleCodeHolder | null } | null>(null);
  const [failure, setFailure] = useState<{ base: string; message: string } | null>(null);

  useEffect(() => {
    if (!active || !base) return;
    let vivant = true;
    const timer = setTimeout(() => {
      api.freeArticleCode(base, true)
        .then((r) => { if (vivant) { setReply({ base, ...r }); setFailure(null); } })
        .catch((e: unknown) => { if (vivant) setFailure({ base, message: e instanceof Error ? e.message : String(e) }); });
    }, CHECK_DELAY_MS);
    return () => { vivant = false; clearTimeout(timer); };
  }, [active, base]);

  // ⚠️ UNE RÉPONSE NE VAUT QUE POUR LA BASE QU'ELLE A LUE : celle du mot précédent se tairait sur le mot courant.
  const r = reply?.base === base ? reply : null;
  const f = failure?.base === base ? failure.message : null;
  const checking = !!base && !r && !f;
  if (saisi) {
    const taken = r ? !r.free : false;
    return {
      effective: saisi, proposed: saisi, holder: r?.holder ?? null,
      freeAlternative: taken ? r!.code : null,
      checking, blocks: checking || taken, failure: f,
    };
  }
  // Sans saisie : le premier libre, ou la base tant qu'on ne sait pas (le serveur refusera s'il est pris).
  const proposed = r?.code ?? base;
  return { effective: proposed, proposed, holder: r?.holder ?? null, freeAlternative: null, checking, blocks: false, failure: f };
}

/**
 * CE QUI S'AFFICHE SOUS LE CHAMP — le comportement, puis ce que le serveur a dit.
 * ⚠️ « AJOUTER UNE DÉCLINAISON À CET ARTICLE » OUVRE SA FICHE, jamais une création ici : sur la fiche, la case nouvelle
 *    hérite du modèle et rien d'autre ne change — c'est le seul chemin où rattacher n'écrase rien.
 */
export function ArticleCodeNotice({ check, typed, onTake, onOpenHolder }: {
  check: ArticleCodeCheck;
  typed: string;
  /** Pose le code libre proposé à la place du code saisi. */
  onTake: (code: string) => void;
  /** Ouvre la fiche de l'article qui porte le code — absent là où l'écran ne sait pas l'ouvrir. */
  onOpenHolder?: (holder: ArticleCodeHolder) => void;
}) {
  const help = { margin: '8px 0 0', fontSize: '11.5px', color: 'var(--ink-400)', lineHeight: 1.5 } as const;
  if (check.failure) {
    return <p role="status" style={{ ...help, color: 'var(--amber-fg)' }}>{t('Code non vérifié : {motif} — la création refusera un code déjà porté.', { motif: check.failure })}</p>;
  }
  const h = check.holder;
  if (typed.trim() && h && check.freeAlternative) {
    return (
      <div role="alert" style={{
        marginTop: 8, padding: '10px 12px', borderRadius: 'var(--r-sm)',
        background: 'var(--amber-bg)', border: '1px solid var(--amber)', fontSize: '12px', color: 'var(--ink-800)', lineHeight: 1.5,
      }}>
        <span style={{ display: 'block' }}>
          {t('Ce code est celui de « {nom} » ({declinaisons}).', { nom: h.name, declinaisons: t('{n} déclinaison', { n: h.variantCount }) })}
        </span>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          <button type="button" className="btn-cta" style={{ height: 30, fontSize: '12px' }} onClick={() => onTake(check.freeAlternative!)}>
            {t('Prendre {code}', { code: check.freeAlternative })}
          </button>
          {onOpenHolder && (
            <button type="button" className="btn-ghost" style={{ height: 30, fontSize: '12px' }} onClick={() => onOpenHolder(h)}>
              {t('Ajouter une déclinaison à cet article')}
            </button>
          )}
        </span>
      </div>
    );
  }
  if (!typed.trim() && h) {
    return <p style={help}>{t('{base} est le code de « {nom} » : {code} est proposé — modifiable.', { base: h.modelKey, nom: h.name, code: check.proposed })}</p>;
  }
  return <p style={help}>{t('Regroupe toutes les tailles et couleurs. Proposé, modifiable.')}</p>;
}
